/**
 * FERP Portal - Apollo Client Configuration
 *
 * Creates Apollo Client with:
 * - HTTP Link to federated GraphQL gateway
 * - WebSocket Link for real-time subscriptions
 * - Auth Link for JWT token injection
 * - Error Link for unified error handling
 * - Cache configuration with type policies
 *
 * CONSTRAINTS:
 * - All subscriptions are tenant-scoped
 * - WebSocket reconnects with exponential backoff
 * - Life-critical subscriptions take priority
 */

// eslint-disable-next-line no-restricted-imports -- Apollo Client configuration module
import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, from, split, Observable  } from '@apollo/client';
// eslint-disable-next-line no-restricted-imports -- Apollo Client configuration module
import { onError } from '@apollo/client/link/error';
// eslint-disable-next-line no-restricted-imports -- Apollo Client configuration module
import { setContext } from '@apollo/client/link/context';
// eslint-disable-next-line no-restricted-imports -- Apollo Client configuration module
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
// eslint-disable-next-line no-restricted-imports -- Apollo Client configuration module
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { getEnv, getGlobalIdToken, getGlobalAuthToken, isMockAuthEnabled } from './env';
import { globalErrorStore, createDetailedError, createNetworkError } from './globalErrorStore';

/**
 * Auth-ready state tracking for race condition prevention.
 *
 * Problem: After OIDC redirect, Apollo queries fire before tokens are in sessionStorage.
 * Solution: Track auth state and wait for tokens when we know callback just completed.
 */
let _authCallbackJustCompleted = false;
let _authReadyPromise: Promise<void> | null = null;
let _authReadyResolve: (() => void) | null = null;

/**
 * Mark that OIDC callback just completed - tokens should appear in sessionStorage soon.
 * Called by AuthContext after maybeHandleRedirectCallback() succeeds.
 */
export function markAuthCallbackCompleted(): void {
  _authCallbackJustCompleted = true;
  // Create a promise that resolves when token appears
  if (!_authReadyPromise) {
    _authReadyPromise = new Promise((resolve) => {
      _authReadyResolve = resolve;
    });
  }
}

/**
 * Mark auth as ready (token confirmed in storage).
 * Called by AuthContext after checkSession() confirms valid tokens.
 */
export function markAuthReady(): void {
  _authCallbackJustCompleted = false;
  if (_authReadyResolve) {
    _authReadyResolve();
    _authReadyResolve = null;
    _authReadyPromise = null;
  }
}

/**
 * Wait for auth token to appear in sessionStorage (max 3 seconds).
 * Only waits if we know callback just completed.
 */
async function waitForAuthToken(): Promise<string | null> {
  const maxWaitMs = 3000;
  const pollIntervalMs = 50;
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    const token = getGlobalAuthToken() || getGlobalIdToken();
    if (token) {
      markAuthReady();
      return token;
    }
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  console.warn('[FERP-AUTH] Timeout waiting for auth token after callback');
  _authCallbackJustCompleted = false;
  return null;
}

/**
 * Get the GraphQL HTTP endpoint from environment
 *
 * Uses relative URL `/graphql` by default so Vite proxy handles routing
 * based on VITE_BACKEND_TARGET (local/staging/production).
 *
 * This avoids CORS issues since requests go through the same origin.
 */
function getGraphQLEndpoint(): string {
  const env = getEnv();

  // Allow explicit override from environment (for non-Vite deployments)
  if (typeof env.GRAPHQL_ENDPOINT === 'string') {
    return env.GRAPHQL_ENDPOINT;
  }

  // Use relative URL so Vite proxy routes based on VITE_BACKEND_TARGET
  // See vite.config.ts for proxy configuration:
  //   local -> http://localhost:8080/graphql
  //   staging -> staging ALB
  //   production -> production Apollo Router
  return '/graphql';
}

/**
 * Get the WebSocket endpoint for subscriptions
 *
 * The gateway serves GraphQL subscriptions at /ws (not /graphql).
 * Vite proxy routes /ws → backend:8080/ws with ws: true.
 * Constructs absolute WebSocket URL since WS can't use relative URLs.
 */
function getWebSocketEndpoint(): string {
  const httpEndpoint = getGraphQLEndpoint();

  // If already an absolute URL, convert http(s) to ws(s) and swap /graphql → /ws
  if (httpEndpoint.startsWith('http://') || httpEndpoint.startsWith('https://')) {
    return httpEndpoint
      .replace(/^http:/, 'ws:')
      .replace(/^https:/, 'wss:')
      .replace(/\/graphql$/, '/ws');
  }

  // For relative URLs, construct WS URL from current window location
  // Gateway WebSocket endpoint is /ws, not /graphql
  if (globalThis.window !== undefined) {
    const protocol = globalThis.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${globalThis.location.host}/ws`;
  }

  // SSR fallback
  return `ws://localhost:8080/ws`;
}

/**
 * Get auth token from storage
 *
 * DRR-260: With Authentik 2024.12+ and `access_token_mode: jwt`, access_token
 * is now a proper JWT containing all claims (ferp_tenant, ferp_roles, ferp_actor_id).
 * This is the preferred token for API auth.
 *
 * Falls back to id_token for backward compatibility with older Authentik versions
 * that use opaque access tokens.
 *
 * The gateway validates the token signature via JWKS and extracts claims.
 */
function getAuthToken(): string | null {
  try {
    // DRR-260: Prefer access_token (now a JWT with Authentik 2024.12+)
    const accessToken = getGlobalAuthToken();
    if (accessToken) {
      return accessToken;
    }

    // Fallback to id_token for backward compatibility
    const idToken = getGlobalIdToken();
    if (idToken) {
      if (import.meta.env.DEV) {
        console.log('⚠️ Using id_token - access_token not available');
      }
      return idToken;
    }
  } catch {
    // Storage access failed
  }
  return null;
}

/**
 * DRR-259: Slug-to-numeric tenant ID mapping for local dev.
 * Backend now requires numeric tenant IDs in x-tenant header.
 */
const TENANT_SLUG_TO_NUM: Record<string, string> = {
  'ferp-system': '0',
  'ferp-operations': '1',
  'hillside-harmony': '2',
  'athanor-alf': '3',
  'omniquark': '4',
  'cc-kitchen': '5',
  'cc-laundry': '6',
  'aahhcs': '7',
  'athanor-wellness': '21',
  'athanor-memorial': '22',
  'phin-personal': '23',
};

/**
 * Get tenant ID from JWT token or storage
 *
 * DRR-259: Returns NUMERIC tenant ID for x-tenant header.
 * Backend requires numeric IDs (e.g., "2" not "hillside-harmony").
 *
 * Extracts tenant from JWT claims (same source as backend uses).
 * In dev mode, DevImpersonationPanel's localStorage override takes priority
 * (matches AuthContext.extractTenantFromToken behavior).
 * Falls back to sessionStorage for backwards compatibility.
 */
function getTenantId(): string | null {
  try {
    // DRR-259: First check for explicit numeric tenant_num override
    if (isDevModeEnabled()) {
      const numOverride = localStorage.getItem('tenant_num');
      if (numOverride) return numOverride;
    }

    // Dev mode: prefer DevImpersonationPanel localStorage override
    // Must match AuthContext.extractTenantFromToken() priority order
    if (isDevModeEnabled()) {
      const lsOverride = localStorage.getItem('tenant_id');
      if (lsOverride) {
        // DRR-259: Convert slug to numeric ID if needed
        return TENANT_SLUG_TO_NUM[lsOverride] || lsOverride;
      }
    }

    // Extract tenant from JWT token (single source of truth in production)
    const token = getAuthToken();
    if (token) {
      try {
        const tokenParts = token.split('.');
        if (!tokenParts[1]) {
          throw new Error('Invalid JWT format');
        }
        const payload = JSON.parse(atob(tokenParts[1]));
        // DRR-259: Prefer numeric tenant_num claim
        // Check both Authentik format (ferp_tenant_num) and generic format (tenant_num)
        const tenantNum = payload?.['ferp_tenant_num'] || payload?.['custom:tenant_num'] || payload?.['tenant_num'];
        if (tenantNum) {
          return String(tenantNum);
        }
        // Fallback to tenant_id claims (for backwards compat)
        const tenant =
          payload?.['ferp_tenant'] ||
          payload?.['custom:tenant_id'] ||
          payload?.['tenant_id'] ||
          payload?.tenant ||
          payload?.['ak_proxy']?.['user_attributes']?.['tenant'] ||
          null;
        if (tenant) {
          // DRR-259: Convert slug to numeric ID if needed
          return TENANT_SLUG_TO_NUM[tenant] || tenant;
        }
      } catch {
        // JWT decode failed, fall through to sessionStorage
      }
    }

    // Fallback to sessionStorage
    const fromSession = sessionStorage.getItem('auth:tenant_id');
    if (fromSession) {
      // DRR-259: Convert slug to numeric ID if needed
      return TENANT_SLUG_TO_NUM[fromSession] || fromSession;
    }

    // Fallback to configured tenant from env.js (DRR-046: local dev default)
    if (typeof window !== 'undefined' && window.__TENANT__) {
      const envTenant = window.__TENANT__;
      // DRR-259: Convert slug to numeric ID if needed
      return TENANT_SLUG_TO_NUM[envTenant] || envTenant;
    }

    // DRR-259: Check for numeric tenant from env.js
    if (typeof window !== 'undefined' && window.__TENANT_NUM__) {
      return String(window.__TENANT_NUM__);
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * HTTP Link to GraphQL Gateway
 */
const httpLink = new HttpLink({
  uri: getGraphQLEndpoint(),
  credentials: 'include', // Include cookies for session auth
});

/**
 * WebSocket Link for subscriptions
 * Uses graphql-ws protocol with automatic reconnection
 */
const wsLink = new GraphQLWsLink(
  createClient({
    url: getWebSocketEndpoint(),
    connectionParams: () => {
      const actorAuthId = getDevActorAuthId();
      const tenantId = getTenantId();

      // Dev mode actor impersonation: stub token + x-ferp-user-id + scopes + role IDs
      if (actorAuthId) {
        const devScopes = getDevActorScopes();
        const devRoleIds = getDevActorRoleIds();
        // DRR-196: Only use default admin scopes when NO actor is selected (first load).
        // When an actor IS selected but has no roles (residents, volunteers), they should
        // get empty scopes, NOT admin access. DevImpersonationPanel removes dev_actor_scopes
        // from localStorage for users with no roles, so devScopes will be null.
        // Check if an actor is explicitly selected (dev_actor_auth_id is set and not default).
        const actorIsSelected = actorAuthId && actorAuthId !== '2_1';
        const defaultDevScopes = 'scope:read,scope:write,task:read,task:write,workflow:read,workflow:write,actor:read,actor:write,team:read,team:write,approval:read,approval:write,document:read,document:write,queue:read,queue:write,event:read,event:write,notification:read,notification:write,audit:read,analytics:read,analytics:write,schedule:read,schedule:write,resource:read,resource:write,integration:read,integration:write,forms:read,forms:write,metadata:read,metadata:write,tenant:admin,facility:admin,clinical:full,operational:full,financial:full,policy:read,policy:write,template:read,template:write,instance:read,instance:write,agent:read,agent:compose,training:self:read,payroll:self:read,billing:read,billing:write,claims:read,claims:submit';
        // If actor is selected with no scopes, send empty. If no actor selected, use defaults.
        const scopesToSend = devScopes || (actorIsSelected ? '' : defaultDevScopes);
        return {
          headers: {
            authorization: 'Bearer dev',
            ...(tenantId ? { 'x-tenant': tenantId } : {}),
            'x-ferp-user-id': actorAuthId,
            ...(scopesToSend ? { 'x-ferp-scopes': scopesToSend } : {}),
            ...(devRoleIds ? { 'x-ferp-role-ids': devRoleIds } : {}),
          },
        };
      }

      const token = getAuthToken();
      return {
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...(tenantId ? { 'x-tenant': tenantId } : {}),
        },
      };
    },
    // Reconnect on connection loss (critical for life-safety systems)
    shouldRetry: () => true,
    retryAttempts: Infinity,
    retryWait: async (retries) => {
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const waitTime = Math.min(1000 * Math.pow(2, retries), 30000);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    },
    on: {
      connected: () => {
        if (import.meta.env.DEV) {
          console.log('🔗 WebSocket connected for real-time subscriptions');
        }
      },
      closed: (event) => {
        if (import.meta.env.DEV) {
          console.log('🔌 WebSocket disconnected', event);
        }
      },
      error: (error) => {
        if (import.meta.env.DEV) {
          console.error('❌ WebSocket error:', error);
        }
      },
    },
    // Keep-alive ping every 30 seconds
    keepAlive: 30000,
  })
);

/**
 * Get dev actor auth ID from localStorage (set by ActorSwitch DevTool).
 * Only used in mock auth / stub gateway mode.
 * Falls back to '2_1' (Hillside Harmony ALD) when ENABLE_MOCK_AUTH is true but no actor selected.
 */
function getDevActorAuthId(): string | null {
  try {
    // CRITICAL: Only use dev actor overrides when BOTH:
    // 1. Dev mode is enabled (ENABLE_DEV_MODE=true OR ENABLE_MOCK_AUTH=true)
    // 2. localStorage has a dev_actor_auth_id set
    // This prevents leftover localStorage from dev sessions affecting production.
    const env = (window as unknown as { __ENV__?: Record<string, string> }).__ENV__;
    const isDevOrMock = env?.ENABLE_DEV_MODE === 'true' || env?.ENABLE_MOCK_AUTH === 'true';

    if (!isDevOrMock) {
      // Production mode — never use dev actor overrides
      return null;
    }

    const storedId = localStorage.getItem('dev_actor_auth_id');
    if (storedId) return storedId;

    // Fallback to Hillside Harmony ALD when mock auth is enabled
    if (env?.ENABLE_MOCK_AUTH === 'true') {
      return '2_1';
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get dev actor scopes from localStorage (DRR-103).
 * Set by DevImpersonationPanel when an actor is selected.
 * Sent as x-ferp-scopes header so gateway uses real role scopes
 * instead of falling back to wildcard "*".
 */
function getDevActorScopes(): string | null {
  try {
    return localStorage.getItem('dev_actor_scopes');
  } catch {
    return null;
  }
}

/**
 * Get dev actor role IDs from localStorage (DRR-103 Phase 2).
 * Set by DevImpersonationPanel when an actor is selected.
 * Sent as x-ferp-role-ids header for lens rule condition matching.
 */
function getDevActorRoleIds(): string | null {
  try {
    return localStorage.getItem('dev_actor_role_ids');
  } catch {
    return null;
  }
}

/**
 * Observer mode flag — set by WorkflowObserverPage when mounted.
 * When active, the auth link injects x-ferp-observer-mode: true header.
 * Backend requires BOTH this header AND workflow:observe scope (dual-gate).
 */
let _observerModeActive = false;

export function setObserverMode(active: boolean): void {
  _observerModeActive = active;
}

export function isObserverModeActive(): boolean {
  return _observerModeActive;
}

/**
 * Generate correlation ID for request tracing (DRR-228)
 * Uses crypto.randomUUID() for collision-resistant IDs.
 * Falls back to timestamp-based ID if crypto.randomUUID unavailable.
 */
function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Current request correlation ID (DRR-228)
 * Set before each request, accessible for error display.
 */
let _currentCorrelationId: string | null = null;

/**
 * Get the current request's correlation ID for error display
 */
export function getCurrentCorrelationId(): string | null {
  return _currentCorrelationId;
}

/**
 * Auth Link - Injects JWT token into requests
 *
 * When ActorSwitch DevTool is active (dev_actor_auth_id in localStorage),
 * sends `Bearer dev` stub token + x-ferp-user-id header instead of the real JWT.
 * This triggers the gateway's stub auth path which resolves the actor from the header.
 * Requires AUTH_ALLOW_STUB=true on the gateway (set in docker-compose.local.yml).
 *
 * Also injects x-ferp-observer-mode header when observer mode is active.
 * DRR-228: Injects x-correlation-id header for E2E tracing.
 */
const authLink = setContext(async (_: any, { headers }) => {
  const actorAuthId = getDevActorAuthId();
  const tenantId = getTenantId();
  const observerHeader = _observerModeActive ? { 'x-ferp-observer-mode': 'true' } : {};

  // DRR-228: Generate correlation ID for E2E tracing
  _currentCorrelationId = generateCorrelationId();

  // Dev mode actor impersonation: use stub token so gateway reads x-ferp-user-id
  // DRR-103: Also send x-ferp-scopes from DEV_ROLE_SCOPES (stored by DevImpersonationPanel)
  // DRR-103 Phase 2: Send x-ferp-role-ids for lens rule condition matching
  // DRR-123: Send x-ferp-active-wall for information barrier enforcement
  const activeWall = localStorage.getItem('dev_active_wall') || '';
  if (actorAuthId) {
    const devScopes = getDevActorScopes();
    const devRoleIds = getDevActorRoleIds();
    // DRR-196: Only use default admin scopes when NO actor is selected (first load).
    // When an actor IS selected but has no roles (residents, volunteers), they should
    // get empty scopes, NOT admin access.
    const actorIsSelected = actorAuthId && actorAuthId !== '2_1';
    const defaultDevScopes = 'scope:read,scope:write,task:read,task:write,workflow:read,workflow:write,actor:read,actor:write,team:read,team:write,approval:read,approval:write,document:read,document:write,queue:read,queue:write,event:read,event:write,notification:read,notification:write,audit:read,analytics:read,analytics:write,schedule:read,schedule:write,resource:read,resource:write,integration:read,integration:write,forms:read,forms:write,metadata:read,metadata:write,tenant:admin,facility:admin,clinical:full,operational:full,financial:full,policy:read,policy:write,template:read,template:write,instance:read,instance:write,agent:read,agent:compose,training:self:read,payroll:self:read,billing:read,billing:write,claims:read,claims:submit';
    const scopesToSend = devScopes || (actorIsSelected ? '' : defaultDevScopes);
    return {
      headers: {
        ...headers,
        authorization: 'Bearer dev',
        ...(tenantId ? { 'x-tenant': tenantId } : {}),
        'x-ferp-user-id': actorAuthId,
        ...(scopesToSend ? { 'x-ferp-scopes': scopesToSend } : {}),
        ...(devRoleIds ? { 'x-ferp-role-ids': devRoleIds } : {}),
        ...(activeWall ? { 'x-ferp-active-wall': activeWall } : {}),
        'x-correlation-id': _currentCorrelationId, // DRR-228
        ...observerHeader,
      },
    };
  }

  let token = getAuthToken();

  // Race condition fix: If callback just completed, wait for token to appear in sessionStorage
  if (!token && _authCallbackJustCompleted) {
    console.log('[FERP-AUTH] Callback just completed, waiting for token...');
    token = await waitForAuthToken();
  }

  // DRR-312 FIX: If on callback page with auth code, wait for token to appear.
  // Requests fire while callback is processing (before markAuthCallbackCompleted is called).
  const isOnCallbackPage = globalThis.location?.pathname?.includes('/auth/callback');
  const hasAuthCode = globalThis.location?.search?.includes('code=');
  if (!token && isOnCallbackPage && hasAuthCode) {
    console.log('[FERP-AUTH] On callback page with code, waiting for token...');
    token = await waitForAuthToken();
  }

  // DEBUG CEO AUTH: Log when no token found (only if not mock auth and not on callback page)
  if (!token && !isMockAuthEnabled() && !isOnCallbackPage) {
    const ssToken = sessionStorage.getItem('auth:token');
    const ssIdToken = sessionStorage.getItem('auth:id_token');
    console.warn('[FERP-AUTH-DEBUG] No auth token found.', {
      sessionStorage_auth_token: ssToken ? `present (${ssToken.length} chars)` : 'missing',
      sessionStorage_auth_id_token: ssIdToken ? `present (${ssIdToken.length} chars)` : 'missing',
      getAuthToken_result: token,
      callbackJustCompleted: _authCallbackJustCompleted,
    });
  }

  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { 'x-tenant': tenantId } : {}),
      'x-correlation-id': _currentCorrelationId, // DRR-228
      ...observerHeader,
    },
  };
});

/**
 * Guard against multiple simultaneous auth redirects.
 * When N queries all get 403 at once, only the first one triggers the redirect.
 */
let isRedirectingToLogin = false;

function redirectToLogin(reason: string): void {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  console.warn(`${reason} - clearing tokens and redirecting to login`);
  try {
    sessionStorage.removeItem('auth:token');
    sessionStorage.removeItem('auth:id_token');
  } catch { /* storage access may fail */ }
  window.location.href = '/login';
}

/**
 * Error Link - Unified error handling with detailed capture
 *
 * **SINGLE CODE PATH**: All GraphQL errors flow through this link.
 * Captures errors to globalErrorStore for detailed debugging in ErrorTracePanel.
 */
const errorLink = onError(({  graphQLErrors, networkError, operation  }: any) => {
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      console.error(
        `[GraphQL Error]: Message: ${error.message}, Location: ${JSON.stringify(error.locations)}, Path: ${error.path}`
      );

      // Capture detailed error for ErrorTracePanel
      try {
        const detailedError = createDetailedError(error, operation);
        globalErrorStore.add(detailedError);
      } catch (captureError) {
        console.error('Failed to capture error details:', captureError);
      }

      // Handle authentication errors (skip in mock auth — spurious from stale tokens)
      if (error.extensions?.code === 'UNAUTHENTICATED' && !isMockAuthEnabled()) {
        redirectToLogin('Authentication error (UNAUTHENTICATED)');
      }
    }
  }

  if (networkError) {
    console.error(`[Network Error]: ${networkError.message}`);

    // Capture network error for ErrorTracePanel
    try {
      const detailedError = createNetworkError(networkError as Error & { statusCode?: number }, operation);
      globalErrorStore.add(detailedError);
    } catch (captureError) {
      console.error('Failed to capture network error details:', captureError);
    }

    // Handle auth-related network errors (401 Unauthorized, 403 Forbidden)
    // Skip redirect in mock auth mode — 401s are spurious (stale OIDC tokens
    // or gateway stub mode rejecting real JWTs). Don't blow away the session.
    if ('statusCode' in networkError) {
      const status = networkError.statusCode;
      if ((status === 401 || status === 403) && !isMockAuthEnabled()) {
        redirectToLogin(`Auth error (HTTP ${status})`);
      }
    }
  }
});

/**
 * Response Error Capture Link
 *
 * Captures errors from responses (handles errorPolicy: 'all' case)
 *
 * With errorPolicy: 'all', errors are included in response.errors but don't
 * always trigger the onError link. This link captures those errors.
 *
 * Note: Uses Observable pattern instead of .map() for Apollo Client v4 compatibility
 */
const responseErrorCaptureLink = new ApolloLink((operation, forward) => {
  // Apollo Client v4: Use Observable pattern instead of .map()
  const observable = forward(operation);

  return new Observable((observer) => {
    const subscription = observable.subscribe({
      next: (result) => {
        // If response has errors, capture them to globalErrorStore
        if (result.errors && result.errors.length > 0) {
          if (import.meta.env.DEV) {
            console.log('🔍 Found errors in response:', result.errors);
          }
          for (const error of result.errors) {
            try {
              // @ts-expect-error Argument type
              const detailedError = createDetailedError(error, operation);
              globalErrorStore.add(detailedError);
            } catch (captureError) {
              console.error('Failed to capture response error:', captureError);
            }
          }
        }

        observer.next(result);
      },
      error: (error) => {
        observer.error(error);
      },
      complete: () => {
        observer.complete();
      },
    });

    // Cleanup on unsubscribe
    return () => {
      subscription.unsubscribe();
    };
  });
});

/**
 * Check if dev mode is enabled
 */
function isDevModeEnabled(): boolean {
  const env = getEnv();
  return env.ENABLE_DEV_MODE === 'true' || env.ENABLE_DEV_MODE === true;
}

/**
 * Logging Link (development only)
 *
 * Note: Uses concat pattern instead of .map() to avoid Apollo Client v4 Observable issues
 */
const loggingLink = new ApolloLink((operation, forward) => {
  if (isDevModeEnabled()) {
    const operationType = getMainDefinition(operation.query);
    const type =
      operationType.kind === 'OperationDefinition'
        ? operationType.operation
        : 'unknown';
    console.log(`[GraphQL ${type}]: ${operation.operationName}`, {
      variables: operation.variables,
    });
  }

  // Apollo Client v4: Don't use .map() on Observable - just return forward()
  const observable = forward(operation);

  if (!isDevModeEnabled()) {
    return observable;
  }

  // For dev mode, subscribe and log responses
  return new Observable((observer) => {
    const subscription = observable.subscribe({
      next: (result) => {
        console.log(`[GraphQL Response]: ${operation.operationName}`, result);
        observer.next(result);
      },
      error: (error) => {
        observer.error(error);
      },
      complete: () => {
        observer.complete();
      },
    });

    return () => subscription.unsubscribe();
  });
});

/**
 * Split Link - Route subscriptions to WebSocket, queries/mutations to HTTP
 */
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,    // WebSocket for subscriptions
  httpLink   // HTTP for queries and mutations
);

/**
 * Apollo Cache with type policies for 25 primitives
 */
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Client-only fields
        isLoggedIn: {
          read() {
            return !!getAuthToken();
          },
        },
        currentViewLayer: {
          read() {
            try {
              return sessionStorage.getItem('portal:viewLayer') || 'USER';
            } catch {
              return 'USER';
            }
          },
        },
      },
    },

    // FormSchema: keyed by subgraph+entityType+version to prevent cache merge warnings
    // when formSchema() query and formSchema().views query return different field sets.
    FormSchema: {
      keyFields: ['subgraph', 'entityType', 'version'],
    },

    // Entity identification for 25 primitives
    Workflow: { keyFields: ['id'] },
    Context: { keyFields: ['id'] },
    Constraint: { keyFields: ['id'] },
    Dependency: { keyFields: ['id'] },
    Form: { keyFields: ['id'] },
    Actor: { keyFields: ['id'] },
    Policy: { keyFields: ['id'] },
    Permission: { keyFields: ['id'] },
    Queue: { keyFields: ['id'] },
    Schedule: { keyFields: ['id'] },
    Task: { keyFields: ['id'] },
    State: { keyFields: ['id'] },
    Event: { keyFields: ['id'] },
    Saga: { keyFields: ['id'] },
    DAGNode: { keyFields: ['id'] },
    Metric: { keyFields: ['id'] },
    Alert: { keyFields: ['id'] },
    Audit: { keyFields: ['id'] },
    Report: { keyFields: ['id'] },
    Notification: { keyFields: ['id'] },
    Contract: { keyFields: ['id'] },
    Compliance: { keyFields: ['id'] },
    Risk: { keyFields: ['id'] },
    Evidence: { keyFields: ['id'] },
    Version: { keyFields: ['id'] },
  },
});

/**
 * Create Apollo Client instance with WebSocket subscription support
 */
export function createApolloClient(): ApolloClient {
  return new ApolloClient({
    link: from([loggingLink, responseErrorCaptureLink, errorLink, authLink, splitLink]),
    cache,
    defaultOptions: {
      watchQuery: {
        // DRR-313: cache-and-network shows cached immediately, then updates
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        // DRR-313: cache-first for static config (was network-only, caused request flood)
        // Individual queries can override to 'network-only' for dynamic data
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    // @ts-expect-error No common properties
    devtools: isDevModeEnabled(),
  });
}

// Export a singleton for use outside React
export const apolloClient = createApolloClient();
